import { useMemo, useState } from 'react';
import { toEndOfUtcDay, toStartOfUtcDay } from '@chainflip/utils/date';
import { differenceInDays, startOfYear, subDays, subMonths, subYears } from 'date-fns';
import { FIRST_DELEGATION_DATE, FIRST_SUPPLY_DATE, FIRST_SWAP_DATE } from '../constants';

const timestamps = {
  firstSwap: FIRST_SWAP_DATE,
  firstSupply: FIRST_SUPPLY_DATE,
  firstDelegation: FIRST_DELEGATION_DATE,
} as const;

type Timeframe = '1D' | '1W' | '1M' | '3M' | 'YTD' | '1Y' | 'All';

export type TimeframeOption = {
  label: Timeframe;
  longLabel: string;
  value: number;
};

export const oneDay = () =>
  ({
    label: '1D',
    longLabel: 'Past day',
    value: 1,
  }) as const satisfies TimeframeOption;

export const oneWeek = () =>
  ({
    label: '1W',
    longLabel: 'Past 7 days',
    value: 7,
  }) as const satisfies TimeframeOption;

export const oneMonth = () =>
  ({
    label: '1M',
    longLabel: 'Past month',
    value: differenceInDays(new Date(), subMonths(new Date(), 1)),
  }) as const satisfies TimeframeOption;

export const threeMonths = () =>
  ({
    label: '3M',
    longLabel: 'Past 3 months',
    value: differenceInDays(new Date(), subMonths(new Date(), 3)),
  }) as const satisfies TimeframeOption;

export const yearToDate = () =>
  ({
    label: 'YTD',
    longLabel: 'Year to date',
    value: differenceInDays(new Date(), startOfYear(new Date())),
  }) as const satisfies TimeframeOption;

export const oneYear = () =>
  ({
    label: '1Y',
    longLabel: 'Past year',
    value: differenceInDays(new Date(), subYears(new Date(), 1)),
  }) as const satisfies TimeframeOption;

export const allTime = (allTimeStartDate: keyof typeof timestamps) =>
  ({
    label: 'All',
    longLabel: 'All time',
    value: differenceInDays(new Date(), new Date(timestamps[allTimeStartDate])),
  }) as const satisfies TimeframeOption;

export const useCustomTimeFrameSelection = <const L extends string>({
  timeframeTabOptions,
  initialTimeframe,
}: {
  timeframeTabOptions: { label: L; value: number; longLabel: string }[];
  initialTimeframe: L;
}) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState(
    timeframeTabOptions.find((tab) => tab.label === initialTimeframe) ?? timeframeTabOptions[0],
  );

  const [chartStartDate, chartEndDate] = useMemo(
    () => [
      toStartOfUtcDay(subDays(new Date(), selectedTimeframe.value)),
      toEndOfUtcDay(new Date()),
    ],
    [selectedTimeframe],
  );
  const daysBack = differenceInDays(chartEndDate, chartStartDate);

  return {
    timeframeTabOptions,
    selectedTimeframe,
    setSelectedTimeframe,
    chartStartDate,
    chartEndDate,
    daysBack,
    isDaily: selectedTimeframe.label === '1D',
    isAllTime: selectedTimeframe.label === 'All',
  };
};

export default function useTimeframeSelection({
  initialTimeframe,
  allTimeStartDate = 'firstSwap',
}: {
  initialTimeframe: Timeframe;
  allTimeStartDate?: keyof typeof timestamps;
}) {
  const timeframeTabOptions: TimeframeOption[] = useMemo(
    () => [
      oneDay(),
      oneWeek(),
      oneMonth(),
      threeMonths(),
      yearToDate(),
      oneYear(),
      allTime(allTimeStartDate),
    ],
    [],
  );

  return useCustomTimeFrameSelection({ timeframeTabOptions, initialTimeframe });
}
